<template>
  <div class="layout">
    <LinkButton
      tag="a"
      to="#main-content"
      class="layout__skip-link"
      secondary
      same-window
      @click.native="handleSkipLink"
    >
      Zum Hauptinhalt springen
    </LinkButton>

    <PageHeader
      :theme="themeHeader"
      :hide-navi="hideNavi"
    />
    <client-only>
      <!-- Show popup only when nav is shown. -->
      <template v-if="themeHeader == null">
        <DefaultPopup />
      </template>
    </client-only>
    <nuxt
      id="main-content"
      class="layout__container"
      tabindex="-1"
    />
    <PageFooter :theme="themeFooter" />
    <client-only>
      <CustomerQueueDialog />
    </client-only>
    <portal-target name="globals" />
    <GlobalModal />
    <template v-if="isDev">
      <StatusToolbarWrapper />
    </template>
  </div>
</template>

<script setup lang="ts">
import { useRuntimeConfig, defineAsyncComponent, useRoute, useRouter } from '#imports';
import { ref, computed, onMounted, onUnmounted, watch, type AsyncComponent } from 'vue';
import { storeToRefs } from 'pinia';
import { debounce } from 'lodash-es';
import PageHeader from '~/components/page-header.vue';
import PageFooter from '~/components/page-footer.vue';
import DefaultPopup from '~/components/default-popup.vue';
import CustomerQueueDialog from '~/components/customer-queue-dialog.vue';
import GlobalModal from '~/components/global-modal.vue';
import { LinkButton } from '@i22/td-longtail-vue-components';

import { useCartStore } from '~/stores/cart';
import { useDowntimesStore } from '~/stores/downtimes';

const config = useRuntimeConfig();

const cartStore = useCartStore();
const downtimesStore = useDowntimesStore();
const { maintenanceDowntime } = storeToRefs(downtimesStore);

function handleSkipLink() {
  setTimeout(() => {
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.focus();
    }
  }, 10);
}

const isDev = !config.public.mode.isProduction;

let StatusToolbarWrapper: AsyncComponent | null = null;

if (isDev) {
  StatusToolbarWrapper = defineAsyncComponent(
    () => import('~/components/status-toolbar-wrapper.vue')
  );
}

defineProps({
  themeHeader: { type: String, default: null },
  themeFooter: { type: String, default: 'standard' },
  hideNavi: { type: Boolean },
});

const hasWindow = () => typeof window !== 'undefined';

const route = useRoute();
const router = useRouter();

const hasVerticalScrollbar = ref(false);

const isPageWithDowntime = computed(() =>
  [
    'aktionsangebote',
    'shop-warenkorb',
    'shop-kundenformular-persoenliche-daten',
    'shop-kundenformular-zahlungsart',
    'shop-zusammenfassung',
    'shop-list',
    'ShopList',
    'magenta-moments-2024',
    'ProductDetails',
    'magenta-tv-hardware',
    'preisvorteile-sichern',
  ].includes(route.name || '')
);

const isMaintenancePage = computed(() => route.name === 'maintenance');

function setViewportSize() {
  if (!hasWindow()) return;
  hasVerticalScrollbar.value = window.innerWidth > document.documentElement.clientWidth;
}

const resizeHandler = debounce(() => {
  setViewportSize();
}, 200);

async function checkForDowntime() {
  if (isMaintenancePage.value) return;
  if (!isPageWithDowntime.value) return;

  await downtimesStore.fetchDowntimes(config);
}

onMounted(async () => {
  if (!hasWindow()) return;

  setViewportSize();
  window.addEventListener('resize', resizeHandler);
  await checkForDowntime();

  if (!route.path?.startsWith('/shop/') && !maintenanceDowntime.value && !isMaintenancePage.value) {
    if (!config.public?.performanceMode?.enabled) {
      await cartStore.initCart();
    }
  }
});

onUnmounted(() => {
  if (!hasWindow()) return;
  window.removeEventListener('resize', resizeHandler);
});

watch(
  () => route,
  async () => {
    await checkForDowntime();
  },
  {
    deep: true,
  }
);

watch(
  maintenanceDowntime,
  async (value) => {
    if (value && isPageWithDowntime.value && !isMaintenancePage.value) {
      await router.push('/maintenance');
      // Recheck required after routing.

      await checkForDowntime();
    }
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  &__container {
    flex: 1;
  }

  &__skip-link {
    position: absolute;
    top: -9999px;
    left: 0;
    z-index: 100;

    &:focus {
      top: 0;
    }
  }
}
</style>
